import React from 'react';

import FieldActionButtons from 'metadata/FieldActionButtons';
import {displayPath} from 'registry/field/FieldPathUtil';
import {SchemaFieldEdge, TargetSchemaToPaths} from 'schemas/fields/SchemaFieldConstants';
import useTagSchemaQuery from 'schemas/useTagSchemaQuery';
import LegacySelectDropdown from 'ui-library/dropdowns/LegacySelectDropdown';

function getLastEdge(orderedPath: Array<SchemaFieldEdge>): SchemaFieldEdge {
  return orderedPath[orderedPath.length - 1];
}

function renderOrderedPath(orderedPath: Array<SchemaFieldEdge>): string {
  return displayPath({sourceTagSchemaId: orderedPath[0].from!.id, tagSchemaFieldEdges: orderedPath});
}

interface HoistedFieldFileSchemaSelectorProps {
  orderedPath?: Array<SchemaFieldEdge> | null;
  targetSchemaToPaths?: TargetSchemaToPaths;
  onUpdateOrderedPath: (option: Array<SchemaFieldEdge>) => void;
}

export function HoistedFieldFileSchemaSelector({
  orderedPath,
  targetSchemaToPaths,
  onUpdateOrderedPath,
}: HoistedFieldFileSchemaSelectorProps): JSX.Element {
  let displayName;

  if (orderedPath) {
    displayName = renderOrderedPath(orderedPath);
  } else if (targetSchemaToPaths) {
    displayName = 'Choose a field...';
  } else {
    displayName = 'Loading...';
  }
  return (
    <LegacySelectDropdown
      disabled={!targetSchemaToPaths}
      displayName={displayName}
      items={
        targetSchemaToPaths
          ? [
              Object.keys(targetSchemaToPaths).map((targetSchemaId) => {
                const paths = targetSchemaToPaths[targetSchemaId];
                return {
                  name: getLastEdge(paths[0]).to!.name,
                  submenu: [
                    paths.map((option) => ({
                      name: renderOrderedPath(option),
                      handleOnClick: () => {
                        onUpdateOrderedPath(option);
                      },
                    })),
                  ],
                };
              }),
            ]
          : []
      }
    />
  );
}

interface HoistedFieldNameSelectorProps {
  hoistedField?: SchemaFieldEdge | null;
  orderedPath?: Array<SchemaFieldEdge> | null;
  onUpdateHoistedField: (option: SchemaFieldEdge) => void;
}

export function HoistedFieldNameSelector({
  hoistedField,
  orderedPath,
  onUpdateHoistedField,
}: HoistedFieldNameSelectorProps): JSX.Element {
  const destinationTagSchemaId =
    orderedPath && orderedPath.length > 0 ? getLastEdge(orderedPath).to!.id : null;
  const {tagSchema} = useTagSchemaQuery(destinationTagSchemaId);

  let displayName;
  if (hoistedField) {
    displayName = hoistedField.name;
  } else if (orderedPath) {
    displayName = 'Choose a field...';
  } else {
    displayName = 'Choose a schema to see valid fields.';
  }

  const fieldOptions = tagSchema != null ? tagSchema.fieldsJson : null;

  return (
    <LegacySelectDropdown
      disabled={!orderedPath}
      displayName={displayName}
      items={
        fieldOptions
          ? [
              fieldOptions.map((option: SchemaFieldEdge) => ({
                name: option.name,
                handleOnClick: () => {
                  onUpdateHoistedField(option);
                },
              })),
            ]
          : []
      }
    />
  );
}

export interface HoistedFieldTableRowProps {
  orderedPath?: Array<SchemaFieldEdge> | null;
  hoistedField?: SchemaFieldEdge | null;
  onComplete?: () => void;
  onDelete: () => void;
  targetSchemaToPaths?: TargetSchemaToPaths;
  onEdit?: (orderedPath: Array<SchemaFieldEdge>, hoistedField: SchemaFieldEdge | null) => void;
  isBeingCreated?: boolean;
  isDisabled?: boolean;
}

export default function HoistedFieldTableRow({
  isBeingCreated = false,
  isDisabled = false,
  orderedPath,
  hoistedField,
  targetSchemaToPaths,
  onComplete,
  onDelete,
  onEdit,
}: HoistedFieldTableRowProps): JSX.Element {
  // TODO(T10284): Remove wrapper `.u-position-relative` when hoisted fields are reorderable
  return (
    <div className="simpleDivTable-row u-position-relative">
      <div className="simpleDivTable-cell">
        <div className="simpleDivTable-cell-contents">
          {isBeingCreated ? (
            <HoistedFieldFileSchemaSelector
              orderedPath={orderedPath}
              targetSchemaToPaths={targetSchemaToPaths}
              onUpdateOrderedPath={(orderedPath) => {
                onEdit!(orderedPath, hoistedField!);
              }}
            />
          ) : (
            displayPath({
              sourceTagSchemaId: orderedPath![0].from!.id,
              tagSchemaFieldEdges: orderedPath!,
            })
          )}
        </div>
      </div>
      <div className="simpleDivTable-cell">
        <div className="simpleDivTable-cell-contents--last">
          {isBeingCreated ? (
            <HoistedFieldNameSelector
              hoistedField={hoistedField}
              orderedPath={orderedPath}
              onUpdateHoistedField={(hoistedField) => {
                onEdit!(orderedPath!, hoistedField);
              }}
            />
          ) : (
            hoistedField!.name
          )}
        </div>
      </div>
      {!isDisabled && (
        <div className="simpleDivTable-actionGroupContainer">
          <FieldActionButtons
            isEditing={isBeingCreated}
            cancel={onDelete}
            complete={onComplete}
            remove={onDelete}
            edit={null}
            isCompleteEnabled={Boolean(isBeingCreated && orderedPath && hoistedField)}
          />
        </div>
      )}
    </div>
  );
}
